import React, { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import employeeForm1 from '../../assets/images/employees/employeeform1.png';

const PersonalInfoForm = ({ onSubmit, data, enableValidation = false }) => {
  const { register, handleSubmit, watch, setValue, reset, unregister, formState: { errors } } = useForm({
    defaultValues: data,
    mode: 'onSubmit'
  });

  const mergeWithExisting = (values) => {
    const merged = {
      ...(typeof data === 'object' && data !== null ? data : {}),
      ...values,
    };
    const isSubmittedFemaleMarried =
      String(merged.gender || '').trim().toLowerCase() === 'female'
      && String(merged.maritalStatus || '').trim().toLowerCase() === 'married';

    return {
      ...merged,
      fatherName: isSubmittedFemaleMarried ? '' : (merged.fatherName || ''),
      spouseName: isSubmittedFemaleMarried ? (merged.spouseName || '') : '',
    };
  };

  const sameAsCurrentAddress = watch('sameAsCurrentAddress');
  const currentAddress = watch('currentAddress');
  const dob = watch('dob');
  const gender = watch('gender');
  const maritalStatus = watch('maritalStatus');

  const calculatedAge = useMemo(() => {
    if (!dob) {
      return '';
    }

    const birthDate = new Date(dob);
    if (Number.isNaN(birthDate.getTime())) {
      return '';
    }

    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const hasHadBirthdayThisYear =
      today.getMonth() > birthDate.getMonth() ||
      (today.getMonth() === birthDate.getMonth() && today.getDate() >= birthDate.getDate());

    if (!hasHadBirthdayThisYear) {
      age -= 1;
    }

    return age >= 0 ? age : '';
  }, [dob]);

  const isFemaleMarried = String(gender || '').trim().toLowerCase() === 'female'
    && String(maritalStatus || '').trim().toLowerCase() === 'married';
  const showSpouseName = isFemaleMarried;
  const showFatherName = !isFemaleMarried;

  useEffect(() => {
    reset(data ?? {});
  }, [data, reset]);

  useEffect(() => {
    if (sameAsCurrentAddress) {
      setValue('permanentAddress', currentAddress || '', { shouldValidate: enableValidation });
    }
  }, [sameAsCurrentAddress, currentAddress, setValue, enableValidation]);

  useEffect(() => {
    const hiddenField = showSpouseName ? 'fatherName' : 'spouseName';
    setValue(hiddenField, '', { shouldDirty: true, shouldValidate: false });
    unregister(hiddenField);
  }, [showSpouseName, setValue, unregister]);

  const getValidationRules = (field) => {
    if (!enableValidation) return {};

    const rules = {
      name: { required: 'Employee name is required' },
      dob: { required: 'Date of birth is required' },
      gender: { required: 'Gender is required' },
      maritalStatus: { required: 'Marital status is required' },
      fatherName: showFatherName ? { required: 'Father Name is required' } : {},
      spouseName: showSpouseName ? { required: 'Spouse Name is required' } : {},
      phone: { required: 'Phone number is required', pattern: { value: /^[0-9]{10}$/, message: 'Phone number must be 10 digits' } },
      aadhar: { required: 'Aadhar number is required' },
      email: { required: 'Email is required', pattern: { value: /^\S+@\S+$/i, message: 'Invalid email format' } },
      currentAddress: { required: 'Current address is required' },
      permanentAddress: { required: 'Permanent address is required' }
    };

    return rules[field] || {};
  };

  return (
    <form onSubmit={handleSubmit((values) => onSubmit({ personal: mergeWithExisting(values) }))}>
      <div className="row">
        <div className="col-md-8">
          <div className="row g-4">
            {/* Employee Name */}
            <div className="col-12">
              <div className="form-floating">
                <input
                  type="text"
                  className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                  id="employeeName"
                  placeholder="Enter Employee Name"
                  {...register('name', getValidationRules('name'))}
                />
                <label htmlFor="employeeName">Enter Employee Name<span className="text-danger">*</span></label>
                {errors.name && <div className="invalid-feedback">{errors.name.message}</div>}
              </div>
            </div>

            {/* Date of Birth */}
            <div className="col-md-6">
              <div className="form-floating">
                <input
                  type="date"
                  className={`form-control ${errors.dob ? 'is-invalid' : ''}`}
                  id="dob"
                  placeholder="Enter Date of Birth"
                  {...register('dob', getValidationRules('dob'))}
                />
                <label htmlFor="dob">Enter Date of Birth<span className="text-danger">*</span></label>
                {errors.dob && <div className="invalid-feedback">{errors.dob.message}</div>}
              </div>
            </div>

            {/* Age (disabled) */}
            <div className="col-md-6">
              <div className="form-floating">
                <input
                  type="text"
                  className="form-control"
                  id="age"
                  placeholder="Age"
                  value={calculatedAge ? `${calculatedAge} years` : ''}
                  readOnly
                />
                <label htmlFor="age">Age</label>
              </div>
            </div>

            {/* Gender */}
            <div className="col-12">
              <div className="form-floating">
                <select
                  className={`form-select ${errors.gender ? 'is-invalid' : ''}`}
                  id="gender"
                  {...register('gender', getValidationRules('gender'))}
                >
                  <option value="">Select Gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
                <label htmlFor="gender">Select your Gender<span className="text-danger">*</span></label>
                {errors.gender && <div className="invalid-feedback">{errors.gender.message}</div>}
              </div>
            </div>

            {/* Marital Status */}
            <div className="col-12">
              <div className="form-floating">
                <select
                  className={`form-select ${errors.maritalStatus ? 'is-invalid' : ''}`}
                  id="maritalStatus"
                  {...register('maritalStatus', getValidationRules('maritalStatus'))}
                >
                  <option value="">Select Marital Status</option>
                  <option value="single">Single</option>
                  <option value="married">Married</option>
                  <option value="divorced">Divorced</option>
                </select>
                <label htmlFor="maritalStatus">Select Marital Status<span className="text-danger">*</span></label>
                {errors.maritalStatus && <div className="invalid-feedback">{errors.maritalStatus.message}</div>}
              </div>
            </div>

            {showFatherName && (
              <div className="col-12">
                <div className="form-floating">
                  <input
                    type="text"
                    className={`form-control ${errors.fatherName ? 'is-invalid' : ''}`}
                    id="fatherName"
                    placeholder="Enter Father Name"
                    {...register('fatherName', getValidationRules('fatherName'))}
                  />
                  <label htmlFor="fatherName">Father Name<span className="text-danger">*</span></label>
                  {errors.fatherName && <div className="invalid-feedback">{errors.fatherName.message}</div>}
                </div>
              </div>
            )}

            {showSpouseName && (
              <div className="col-12">
                <div className="form-floating">
                  <input
                    type="text"
                    className={`form-control ${errors.spouseName ? 'is-invalid' : ''}`}
                    id="spouseName"
                    placeholder="Enter Spouse Name"
                    {...register('spouseName', getValidationRules('spouseName'))}
                  />
                  <label htmlFor="spouseName">Spouse Name<span className="text-danger">*</span></label>
                  {errors.spouseName && <div className="invalid-feedback">{errors.spouseName.message}</div>}
                </div>
              </div>
            )}

            {/* Phone */}
            <div className="col-12">
              <div className="form-floating">
                <div className="input-group">
                  <span className="input-group-text">+91</span>
                  <div className="form-floating flex-grow-1">
                    <input
                      type="tel"
                      className={`form-control ${errors.phone ? 'is-invalid' : ''}`}
                      id="phone"
                      placeholder="Enter Phone Number"
                      {...register('phone', getValidationRules('phone'))}
                    />
                    <label htmlFor="phone">Enter Phone Number<span className="text-danger">*</span></label>
                    {errors.phone && <div className="invalid-feedback">{errors.phone.message}</div>}
                  </div>
                </div>
              </div>
            </div>

            {/* Aadhar */}
            <div className="col-12">
              <div className="form-floating">
                <input
                  type="text"
                  className={`form-control ${errors.aadhar ? 'is-invalid' : ''}`}
                  id="aadhar"
                  placeholder="Enter Aadhar Number"
                  {...register('aadhar', getValidationRules('aadhar'))}
                />
                <label htmlFor="aadhar">Enter Aadhar Number<span className="text-danger">*</span></label>
                {errors.aadhar && <div className="invalid-feedback">{errors.aadhar.message}</div>}
              </div>
            </div>

            {/* Email */}
            <div className="col-12">
              <div className="form-floating">
                <input
                  type="email"
                  className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                  id="email"
                  placeholder="Mail Id"
                  {...register('email', getValidationRules('email'))}
                />
                <label htmlFor="email">Mail Id<span className="text-danger">*</span></label>
                {errors.email && <div className="invalid-feedback">{errors.email.message}</div>}
              </div>
            </div>

            {/* Current Address */}
            <div className="col-12">
              <div className="form-floating">
                <textarea
                  className={`form-control ${errors.currentAddress ? 'is-invalid' : ''}`}
                  id="currentAddress"
                  placeholder="Current Address"
                  style={{ height: '100px' }}
                  {...register('currentAddress', getValidationRules('currentAddress'))}
                ></textarea>
                <label htmlFor="currentAddress">Current Address<span className="text-danger">*</span></label>
                {errors.currentAddress && <div className="invalid-feedback">{errors.currentAddress.message}</div>}
              </div>
            </div>

            {/* Permanent Address */}
            <div className="col-12">
              <div className="form-floating">
                <textarea
                  className={`form-control ${errors.permanentAddress ? 'is-invalid' : ''}`}
                  id="permanentAddress"
                  placeholder="Permanent Address"
                  style={{ height: '100px' }}
                  {...register('permanentAddress', getValidationRules('permanentAddress'))}
                ></textarea>
                <label htmlFor="permanentAddress">Permanent Address<span className="text-danger">*</span></label>
                {errors.permanentAddress && <div className="invalid-feedback">{errors.permanentAddress.message}</div>}
              </div>
              <div className="form-check mt-2">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="sameAddress"
                  {...register('sameAsCurrentAddress')}
                />
                <label className="form-check-label text-muted" htmlFor="sameAddress">
                  Select the box if current address as permanent Address
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Right Image - Fixed at bottom */}
        <div className="col-md-4">
          <div className="position-fixed bottom-25 end-0 d-none d-md-block" style={{ maxWidth: '400px', zIndex: 1 }}>
            <img 
              src={employeeForm1} 
              alt="Employee Form" 
              className="img-fluid" 
              style={{ maxHeight: '50vh' }} 
            />
          </div>
        </div>
      </div>

      {/* Submit Button */}
      <div className="mt-4 text-end">
        <button type="submit" className="btn btn-danger px-4">
          Next
        </button>
       
      </div>
    </form>
  );
};

export default PersonalInfoForm;
